import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useAppSettingsQuery } from './useAppSettingsQuery'
import { useSaveAppSettings } from './useSaveAppSettings'

export interface AppSettings {
  maxStamps: number
  receiptReferenceEnabled: boolean
  referralProgramEnabled: boolean
  cashierStampCorrectionEnabled: boolean
  cashierRewardRestoreEnabled: boolean
}

const DEFAULT_SETTINGS: AppSettings = {
  maxStamps: 20,
  receiptReferenceEnabled: false,
  referralProgramEnabled: true,
  cashierStampCorrectionEnabled: true,
  cashierRewardRestoreEnabled: true,
}

export function useAppSettings() {
  const settingsQuery = useAppSettingsQuery()
  const saveMutation = useSaveAppSettings()

  const form = useForm<AppSettings>({
    defaultValues: DEFAULT_SETTINGS,
    mode: 'onChange',
  })

  useEffect(() => {
    if (settingsQuery.data) form.reset(settingsQuery.data)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data])

  const hasChanges = form.formState.isDirty
  const [pendingSettings, setPendingSettings] = useState<AppSettings | null>(null)

  const requestSave = form.handleSubmit((values) => {
    saveMutation.reset()
    setPendingSettings(values)
  })

  function confirmSave() {
    if (!pendingSettings) return
    saveMutation.mutate(pendingSettings, {
      onSuccess: () => {
        form.reset(pendingSettings)
        setPendingSettings(null)
      },
    })
  }

  function cancelSave() {
    setPendingSettings(null)
    saveMutation.reset()
  }

  return {
    control: form.control,
    hasChanges,
    savedSettings: settingsQuery.data ?? DEFAULT_SETTINGS,
    pendingSettings,
    isLoading: settingsQuery.isLoading,
    isError: settingsQuery.isError,
    isSaving: saveMutation.isPending,
    saveError: saveMutation.isError,
    onRetry: () => void settingsQuery.refetch(),
    requestSave,
    confirmSave,
    cancelSave,
  }
}